import { Vector2, Vector3 } from 'three';

import { LineReference, snapDirectionToAngle } from './line-constraints';

/**
 * How far (meters) the point may be from a snapped direction and still count as snapped,
 * e.g. when it is attached to a point close to the snapped direction.
 */
const snapTolerance = 0.01;

const east = new Vector2(1, 0);
const north = new Vector2(0, 1);
const west = new Vector2(-1, 0);
const south = new Vector2(0, -1);

export type AngleGuide = {
  /** Where the angle is measured, the start of the new line. */
  origin: Vector3;
  /** The line that the angle is measured from, given by a point on it and its direction. */
  referenceLine: { point: Vector3; direction: Vector3 };
  /** Set if there is no reference line, then the angle is measured from north/south or west/east. */
  axis?: 'northSouth' | 'westEast';
  /** Direction in the horizontal plane of the half of the reference line that the angle is measured from. */
  from: Vector2;
  /** Angle (radians) in the horizontal plane from `from` to the new line, counter-clockwise is positive. */
  angle: number;
};

const signedAngle = (from: Vector2, to: Vector2) => Math.atan2(from.cross(to), from.dot(to));

/**
 * Describes how a line from origin to point is snapped to an angle from the reference,
 * or returns undefined if the line is not snapped to any of the angles (radians) from the reference direction.
 *
 * The angle is measured from the closest half of the reference line, so it is at most 90 degrees.
 * Without a reference line it is measured from west/east, or shown along north/south if the line
 * goes north or south.
 */
export const getAngleGuide = (
  origin: Vector3,
  point: Vector3,
  reference: LineReference,
  angles: number[],
): AngleGuide | undefined => {
  const direction = new Vector2(point.x - origin.x, point.y - origin.y);

  if (direction.length() < snapTolerance) {
    return undefined;
  }

  const snappedDirection = snapDirectionToAngle(direction, reference.direction, angles);

  if (snappedDirection.distanceTo(direction) > snapTolerance) {
    return undefined;
  }

  // Without a reference line, the angles are snapped from east, so they are measured from west/east.
  const [forward, backward] = reference.line
    ? [reference.direction, reference.direction.clone().negate()]
    : [east, west];

  let from = forward;
  let angle = signedAngle(forward, snappedDirection);
  const backwardAngle = signedAngle(backward, snappedDirection);

  // Keep the forward half on ties, so that the guide does not flip between them.
  if (Math.abs(backwardAngle) < Math.abs(angle) - 1e-9) {
    from = backward;
    angle = backwardAngle;
  }

  // A line going north or south is shown along the north/south axis instead.
  const alongNorthSouth = reference.line
    ? undefined
    : [north, south].find((x) => Math.abs(signedAngle(x, snappedDirection)) < 1e-6);

  if (alongNorthSouth) {
    from = alongNorthSouth;
    angle = 0;
  }

  if (reference.line) {
    const { start, end } = reference.line;

    return {
      origin: origin.clone(),
      referenceLine: { point: start.clone(), direction: end.clone().sub(start).normalize() },
      from: from.clone(),
      angle,
    };
  }

  const axis = from === north || from === south ? 'northSouth' : 'westEast';

  return {
    origin: origin.clone(),
    referenceLine: {
      point: origin.clone(),
      direction: axis === 'northSouth' ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0),
    },
    axis,
    from: from.clone(),
    angle,
  };
};
